'use client'

import { useEffect, useRef, useState } from 'react'
import type { FormField, ReservationFormConfig } from '@/lib/reservationForm'
import { ADMIN_API, lienDeRedirection } from '@/lib/reservationForm'
import { cn } from '@/lib/utils'

interface ReservationModalProps {
  open: boolean
  onClose: () => void
  /**
   * Config du formulaire chargée par l'appelant (`PricingSection`) via
   * `getReservationForm()`.
   * - `undefined` : chargement en cours.
   * - `null`      : CRM injoignable ou config absente → repli gracieux.
   * - objet       : formulaire à rendre.
   */
  config: ReservationFormConfig | null | undefined
  /** Noms de toutes les formules de la section, pour le champ type `formule`. */
  formules: string[]
  /** Formule pré-sélectionnée (celle dont le bouton a été cliqué). */
  formuleParDefaut?: string
}

const CONTACT_EMAIL = 'contact@rstars.fr'

function valeurParDefaut(field: FormField, formuleParDefaut?: string): string {
  if (field.type === 'formule' && formuleParDefaut) return formuleParDefaut
  return ''
}

export default function ReservationModal({ open, onClose, config, formules, formuleParDefaut }: ReservationModalProps) {
  const [values, setValues] = useState<Record<string, string>>({})
  const [website, setWebsite] = useState('') // honeypot
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [errorMsg, setErrorMsg] = useState('')
  const dialogRef = useRef<HTMLDivElement>(null)
  const firstFieldRef = useRef<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(null)

  // Réinitialise les valeurs à chaque ouverture / changement de config,
  // en pré-sélectionnant la formule cliquée sur le champ `type: 'formule'`.
  useEffect(() => {
    if (!open || !config) return
    const initial: Record<string, string> = {}
    for (const field of config.fields) {
      initial[field.id] = valeurParDefaut(field, formuleParDefaut)
    }
    setValues(initial)
    setErrors({})
  }, [open, config, formuleParDefaut])

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      setTimeout(() => firstFieldRef.current?.focus(), 80)
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const handleClose = () => {
    onClose()
    setTimeout(() => {
      setValues({})
      setWebsite('')
      setErrors({})
      setStatus('idle')
      setErrorMsg('')
    }, 300)
  }

  const setValue = (id: string, v: string) => {
    setValues((prev) => ({ ...prev, [id]: v }))
    setErrors((prev) => {
      if (!prev[id]) return prev
      const next = { ...prev }
      delete next[id]
      return next
    })
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!config || status === 'submitting') return
    if (website) return // honeypot rempli → silence, on ne soumet rien

    const nextErrors: Record<string, string> = {}
    for (const field of config.fields) {
      if (field.required && !(values[field.id] ?? '').trim()) {
        nextErrors[field.id] = 'Ce champ est requis.'
      }
    }
    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors)
      return
    }

    setStatus('submitting')
    setErrorMsg('')
    try {
      const res = await fetch(`${ADMIN_API}/api/public/reservation-request`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ values, honeypot: website || undefined }),
      })
      if (!res.ok) {
        let serverErrors: Record<string, string> | undefined
        try {
          const data = await res.json()
          serverErrors = data?.errors
        } catch {
          // pas de corps JSON exploitable — message générique plus bas
        }
        if (serverErrors && Object.keys(serverErrors).length > 0) {
          setErrors(serverErrors)
          setStatus('error')
          setErrorMsg('Merci de corriger les champs indiqués.')
          return
        }
        throw new Error(`HTTP ${res.status}`)
      }
      setStatus('success')
      // Demande enregistrée (lead + mail partis côté CRM) : si un lien de
      // billetterie est réglé, on y emmène le visiteur. Le message de succès
      // reste affiché le temps du chargement, avec le lien en secours.
      const redirection = lienDeRedirection(config)
      if (redirection) window.location.assign(redirection)
    } catch (err) {
      setStatus('error')
      setErrorMsg(err instanceof Error ? err.message : 'Erreur réseau')
    }
  }

  if (!open) return null

  // Repli gracieux : config introuvable/injoignable → petit mot + mailto,
  // la page Formules ne casse jamais.
  if (config === null) {
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="reservation-indispo-title"
        className="fixed inset-0 z-[2000] flex items-center justify-center px-4 py-8"
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} aria-hidden />
        <div
          ref={dialogRef}
          className="relative w-full max-w-md bg-brand-nuit border border-brand-accent/20 px-8 py-10 md:px-10 md:py-12 shadow-2xl text-center"
        >
          <button
            type="button"
            onClick={handleClose}
            aria-label="Fermer"
            className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-brand-cream/60 hover:text-brand-cream transition-colors"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
          <h2 id="reservation-indispo-title" className="font-serif text-xl md:text-2xl text-brand-cream font-normal leading-[1.3] mb-4">
            Formulaire momentanément indisponible
          </h2>
          <p className="text-sm text-brand-cream/70 leading-[1.7] font-normal mb-8">
            Écrivez-nous directement à{' '}
            <a href={`mailto:${CONTACT_EMAIL}`} className="text-brand-accent underline underline-offset-4">
              {CONTACT_EMAIL}
            </a>{' '}
            et nous revenons vers vous rapidement.
          </p>
          <button type="button" onClick={handleClose} className="gde-btn gde-btn--on-dark">
            Fermer
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reservation-title"
      className="fixed inset-0 z-[2000] flex items-center justify-center px-4 py-8"
      onClick={(e) => {
        if (e.target === dialogRef.current?.parentElement) handleClose()
      }}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={handleClose} aria-hidden />

      <div
        ref={dialogRef}
        className="relative w-full max-w-md bg-brand-nuit border border-brand-accent/20 px-8 py-10 md:px-10 md:py-12 shadow-2xl max-h-[90vh] overflow-y-auto"
        style={{ animation: 'reservationModalIn 0.3s ease-out' }}
      >
        <style>{`
          @keyframes reservationModalIn {
            from { opacity: 0; transform: translateY(12px); }
            to { opacity: 1; transform: translateY(0); }
          }
        `}</style>

        <button
          type="button"
          onClick={handleClose}
          aria-label="Fermer"
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center text-brand-cream/60 hover:text-brand-cream transition-colors"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        {status === 'success' ? (
          <div className="text-center py-4">
            <div className="w-12 h-px bg-brand-accent mx-auto mb-8" />
            <h2 id="reservation-title" className="font-serif text-2xl md:text-3xl text-brand-cream font-normal leading-[1.3] mb-6">
              Votre demande a bien été reçue
            </h2>
            {lienDeRedirection(config) ? (
              <>
                <p className="text-sm text-brand-cream/70 leading-[1.7] font-normal mb-8">
                  Redirection vers la billetterie…
                </p>
                <a href={lienDeRedirection(config)!} className="gde-btn gde-btn--solid">
                  Accéder à la billetterie
                </a>
              </>
            ) : (
              <>
                <p className="text-sm text-brand-cream/70 leading-[1.7] font-normal mb-8">{config?.successMessage}</p>
                <button type="button" onClick={handleClose} className="gde-btn gde-btn--on-dark">
                  Fermer
                </button>
              </>
            )}
          </div>
        ) : config === undefined ? (
          <div className="text-center py-10">
            <p className="text-sm text-brand-cream/50">Chargement du formulaire…</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-8">
              <p className="text-[11px] tracking-[0.4em] text-brand-accent/70 uppercase mb-4">Réservation</p>
              <h2 id="reservation-title" className="font-serif text-2xl md:text-3xl text-brand-cream font-normal leading-[1.3]">
                Réserver ma place
              </h2>
              <div className="w-10 h-px bg-brand-accent/60 mx-auto mt-6 mb-6" />
              {config.intro && (
                <p className="text-sm text-brand-cream/65 leading-[1.7] font-normal">{config.intro}</p>
              )}
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Honeypot — champ caché, un humain ne le remplit jamais */}
              <input
                type="text"
                name="website"
                value={website}
                onChange={(e) => setWebsite(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                aria-hidden
              />

              {config.fields.map((field, idx) => {
                const inputId = `reservation-${field.id}`
                const commonClass =
                  'w-full bg-transparent border-b border-brand-cream/20 px-0 py-2 text-brand-cream placeholder:text-brand-cream/30 focus:border-brand-accent focus:outline-none transition-colors text-sm'
                const label = (
                  <label htmlFor={inputId} className="block text-[10px] tracking-[0.3em] text-brand-cream/60 uppercase mb-2">
                    {field.label}
                    {field.required && ' *'}
                  </label>
                )
                const error = errors[field.id] && (
                  <p className="text-xs text-red-300/80 mt-1.5">{errors[field.id]}</p>
                )

                if (field.type === 'formule') {
                  return (
                    <div key={field.id}>
                      {label}
                      <select
                        ref={idx === 0 ? (firstFieldRef as React.RefObject<HTMLSelectElement>) : undefined}
                        id={inputId}
                        value={values[field.id] ?? ''}
                        onChange={(e) => setValue(field.id, e.target.value)}
                        className={cn(commonClass, 'bg-brand-nuit')}
                      >
                        <option value="" disabled>
                          Choisir une formule
                        </option>
                        {formules.map((nom) => (
                          <option key={nom} value={nom}>
                            {nom}
                          </option>
                        ))}
                      </select>
                      {error}
                    </div>
                  )
                }

                if (field.type === 'select') {
                  return (
                    <div key={field.id}>
                      {label}
                      <select
                        ref={idx === 0 ? (firstFieldRef as React.RefObject<HTMLSelectElement>) : undefined}
                        id={inputId}
                        value={values[field.id] ?? ''}
                        onChange={(e) => setValue(field.id, e.target.value)}
                        className={cn(commonClass, 'bg-brand-nuit')}
                      >
                        <option value="" disabled>
                          Choisir
                        </option>
                        {(field.options ?? []).map((opt) => (
                          <option key={opt} value={opt}>
                            {opt}
                          </option>
                        ))}
                      </select>
                      {error}
                    </div>
                  )
                }

                if (field.type === 'textarea') {
                  return (
                    <div key={field.id}>
                      {label}
                      <textarea
                        ref={idx === 0 ? (firstFieldRef as React.RefObject<HTMLTextAreaElement>) : undefined}
                        id={inputId}
                        value={values[field.id] ?? ''}
                        onChange={(e) => setValue(field.id, e.target.value)}
                        rows={3}
                        className={commonClass}
                      />
                      {error}
                    </div>
                  )
                }

                // text | email | tel | number
                return (
                  <div key={field.id}>
                    {label}
                    <input
                      ref={idx === 0 ? (firstFieldRef as React.RefObject<HTMLInputElement>) : undefined}
                      id={inputId}
                      type={field.type}
                      value={values[field.id] ?? ''}
                      onChange={(e) => setValue(field.id, e.target.value)}
                      className={commonClass}
                    />
                    {error}
                  </div>
                )
              })}

              {status === 'error' && (
                <p className="text-xs text-red-300/80 leading-relaxed pt-2">
                  {errorMsg || 'Une erreur est survenue. Merci de réessayer dans un instant.'}
                </p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting'}
                className="gde-btn gde-btn--solid mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ width: '100%' }}
              >
                {status === 'submitting' ? 'Envoi en cours…' : config.submitLabel}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  )
}
